/**
 * OutreachPartners — the outreach logo wall.
 *
 * A single shared section used in two places, so the roster never drifts:
 *   · Home  — rendered inside the Partners (sponsors) section
 *   · /partners — rendered under the sponsorship tiers
 *
 * Eight slots in a responsive grid (4 / 3 / 2 columns). Logos live in
 * `public/images/outreach/` — see the README there for filenames and specs.
 * A tile whose image is missing or fails to load falls back to a text
 * wordmark, so the section never shows a broken image.
 */

import React, { useEffect, useRef, useState } from 'react';
import '../ComponentsStyling/OutreachPartners.css';

/* ------------------------------------------------------------------ */
/* Roster — edit here (name / logo / href) and both pages follow.      */
/* href is optional; omit it and the tile renders as a plain card.     */
/* ------------------------------------------------------------------ */

/* Ai Hub was removed from outreach — it is a Silver sponsor now, and listing
   it in both places double-counted the same organization. */
export const OUTREACH_PARTNERS = [
    {
        name: 'IEEE Hashemite University Student Branch',
        short: 'IEEE HU Student Branch',
        logo: '/images/outreach/cropped-HU-LOGOO-1.jpg',
        href: '',
    },
    {
        name: 'GDG on Campus',
        short: 'GDG on Campus',
        logo: '/images/outreach/GDG-GUJ.PNG',
        href: '',
    },
    {
        name: 'IEEE Computer Society — JUST Student Chapter',
        short: 'IEEE CS JUST Chapter',
        logo: '/images/outreach/ieeeCSSocityJUST.png',
        href: '',
    },
    
];

/* ------------------------------------------------------------------ */
/* Tile                                                                */
/* ------------------------------------------------------------------ */

const OutreachTile = ({ partner, index }) => {
    const [failed, setFailed] = useState(false);

    /* `short` keeps the one-line footer caption readable; `name` stays the
       full legal name for alt text and the hover title. */
    const label = partner.short || partner.name;
    const linked = Boolean(partner.href);
    const Wrapper = linked ? 'a' : 'div';
    const wrapperProps = linked
        ? { href: partner.href, target: '_blank', rel: 'noopener noreferrer' }
        : {};

    return (
        <li className="otr__cell" style={{ '--i': index }}>
            <Wrapper
                {...wrapperProps}
                className={`otr__tile${linked ? ' otr__tile--link' : ''}`}
                title={partner.name}
            >
                <span className="otr__tile-corner otr__tile-corner--tl" aria-hidden />
                <span className="otr__tile-corner otr__tile-corner--tr" aria-hidden />
                <span className="otr__tile-corner otr__tile-corner--bl" aria-hidden />
                <span className="otr__tile-corner otr__tile-corner--br" aria-hidden />
                <span className="otr__tile-sweep" aria-hidden />

                <span className="otr__tile-id" aria-hidden>
                    OUT/{String(index + 1).padStart(2, '0')}
                </span>

                <div className="otr__tile-media">
                    {partner.logo && !failed ? (
                        <img
                            className="otr__tile-logo"
                            src={partner.logo}
                            alt={partner.name}
                            loading="lazy"
                            decoding="async"
                            onError={() => setFailed(true)}
                        />
                    ) : (
                        <span className="otr__tile-wordmark">{label}</span>
                    )}
                </div>

                <div className="otr__tile-foot">
                    <span className="otr__tile-name">{label}</span>
                    {linked && (
                        <span className="otr__tile-arrow" aria-hidden>
                            <svg viewBox="0 0 16 16" fill="none">
                                <path d="M3 13L13 3M13 3H6M13 3v7"
                                    stroke="currentColor"
                                    strokeWidth="1.4"
                                    strokeLinecap="square" />
                            </svg>
                        </span>
                    )}
                </div>
            </Wrapper>
        </li>
    );
};

/* ------------------------------------------------------------------ */
/* Section                                                             */
/* ------------------------------------------------------------------ */

const OutreachPartners = ({
    partners = OUTREACH_PARTNERS,
    marker = 'OUTREACH',
    title = 'Outreach partners',
    lede = 'The communities, media, and student organizations helping AI Expo Jordan 2026 reach every corner of Jordan’s tech scene.',
    id = 'outreach',
    /* `divider` draws the hairline + top spacing that separates the wall from
       the sponsor content above it. Turn it off when the host page already
       provides its own section scaffold. */
    divider = true,
}) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting) {
                    setVisible(true);
                    obs.unobserve(el);
                }
            });
        }, { threshold: 0.12 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    if (!partners.length) return null;

    return (
        <section
            ref={ref}
            id={id}
            className={`otr${divider ? '' : ' otr--flush'}${visible ? ' is-visible' : ''}`}
            aria-labelledby={`${id}-title`}
        >
            <div className="otr__head">
                <div className="otr__head-marker">
                    <span className="otr__head-id">[ {marker} ]</span>
                    <h2 className="otr__head-title" id={`${id}-title`}>{title}</h2>
                </div>
                <span className="otr__head-rule" aria-hidden />
                <span className="otr__head-count">
                    {String(partners.length).padStart(2, '0')}&nbsp;partners
                </span>
            </div>

            {lede && <p className="otr__lede">{lede}</p>}

            <ul className="otr__grid">
                {partners.map((p, i) => (
                    <OutreachTile key={p.name || i} partner={p} index={i} />
                ))}
            </ul>
        </section>
    );
};

export default OutreachPartners;
